/**
 * Blog index. Stays noindexed and out of the nav until reviewed posts exist.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import SEO, { breadcrumbs } from '../components/common/SEO'
import PageHero from '../components/common/PageHero'
import BlogCard from '../components/common/BlogCard'
import { blogPosts } from '../data/blog'
import { teamBySlug } from '../data/team'
import { SITE_URL } from '../data/site'

const publishedPosts = blogPosts.filter((p) => p.reviewed)
const categories = ['All', ...new Set(publishedPosts.map((post) => post.category))]

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All')

  const filteredPosts =
    selectedCategory === 'All'
      ? publishedPosts
      : publishedPosts.filter((post) => post.category === selectedCategory)

  return (
    <>
      <SEO
        title="Blog — Movement, Recovery & Wellness"
        description="Articles on massage therapy, athletic therapy, acupuncture, and recovery from the Recreo Rehab team at Yonge & Eglinton, Toronto."
        path="/blog"
        noIndex={publishedPosts.length === 0}
        structuredData={[
          breadcrumbs([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'Recreo Rehab Blog',
            url: `${SITE_URL}/blog`,
            publisher: { '@id': `${SITE_URL}/#organization` },
            blogPost: publishedPosts.map((post) => ({
              '@type': 'BlogPosting',
              headline: post.title,
              url: `${SITE_URL}/blog/${post.slug}`,
              datePublished: post.date,
              author: { '@id': `${SITE_URL}/team/${post.authorSlug}#person`, name: teamBySlug(post.authorSlug)?.name },
            })),
          },
        ]}
      />

      <div className="bg-cream-200">
        <PageHero
          title="Editorial & Insights"
          subtitle="Thoughts on movement, wellness, and recovery"
          size="md"
          texture="green-linen"
        />
      </div>

      <section className="py-20 md:py-28 bg-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {publishedPosts.length === 0 ? (
            <div className="text-center max-w-xl mx-auto">
              <p className="font-sans text-dark-500 text-lg leading-relaxed mb-6">
                Our practitioners are writing their first articles. In the meantime, explore the
                conditions we treat.
              </p>
              <Link
                to="/conditions"
                className="inline-block border border-dark-700 text-dark-700 rounded-full px-6 py-3 font-sans text-xs tracking-[0.15em] uppercase hover:bg-dark-700 hover:text-white transition-all"
              >
                Conditions we treat
              </Link>
            </div>
          ) : (
            <>
              <motion.div
                className="flex flex-wrap gap-3 mb-16 justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
              >
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-5 py-2 font-sans text-xs tracking-[0.1em] uppercase rounded-full transition-all duration-200 border ${
                      selectedCategory === category
                        ? 'bg-brand-orange-500 text-white border-brand-orange-500'
                        : 'bg-transparent text-dark-500 border-dark-300 hover:border-brand-orange-400 hover:text-brand-orange-500'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {filteredPosts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  )
}

export default BlogPage
